/**
 * CombatResolver.ts — Pure combat logic.
 *
 * Resolves each round of combat: damage calculation, stance shifts,
 * ability execution, item usage, and flee attempts. Stateless — takes
 * combatant states and returns results.
 */

import type {
  CombatAction,
  CombatantState,
  CombatAbility,
  EnemyDefinition,
  RoundResult,
  CombatResult,
  CombatOutcome,
  CombatStance,
  ActiveEffect,
} from './CombatTypes';
import {
  getStanceModifiers,
  shiftStance,
  calculateStanceShift,
} from './StanceSystem';

// ─── Constants ──────────────────────────────────────────────────

const BASE_DAMAGE = 3;
const DEFENSE_DAMAGE_REDUCTION = 0.5;
const FLEE_BASE_CHANCE = 0.3;
const FLEE_STANCE_BONUS = 0.15; // per flee modifier point

// ─── Round Resolution ───────────────────────────────────────────

export interface ResolveRoundInput {
  round: number;
  player: CombatantState;
  enemy: CombatantState;
  playerAction: CombatAction;
  enemyAction: CombatAction;
  playerAttack: number;  // base attack from stats
  playerDefense: number; // base defense from stats
  enemyDef: EnemyDefinition;
  ability?: CombatAbility;
}

export function resolveRound(input: ResolveRoundInput): RoundResult {
  const {
    round, player, enemy,
    playerAction, enemyAction,
    playerAttack, playerDefense,
    enemyDef, ability,
  } = input;

  let playerDamageDealt = 0;
  let playerDamageTaken = 0;
  let playerFrostbiteGain = 0;
  let narrativeParts: string[] = [];
  let abilityUsed: string | undefined;
  let itemUsed: string | undefined;

  // Get stance modifiers
  const playerStanceMods = getStanceModifiers(player.stance);
  const enemyStanceMods = getStanceModifiers(enemy.stance);

  // Get active effect modifiers
  const playerEffectAtk = sumEffectModifiers(player.effects, 'attack');
  const playerEffectDef = sumEffectModifiers(player.effects, 'defense');
  const enemyEffectAtk = sumEffectModifiers(enemy.effects, 'attack');
  const enemyEffectDef = sumEffectModifiers(enemy.effects, 'defense');

  // ─── Player Action ──────────────────────────────────────

  switch (playerAction.type) {
    case 'attack': {
      const totalAtk = playerAttack + playerStanceMods.attack + playerEffectAtk;
      const enemyDef = enemyStanceMods.defense + enemyEffectDef + input.enemyDef.defense;
      const rawDmg = Math.max(1, totalAtk - enemyDef);
      // If enemy is defending, reduce damage
      const finalDmg = enemyAction.type === 'defend'
        ? Math.max(1, Math.floor(rawDmg * DEFENSE_DAMAGE_REDUCTION))
        : rawDmg;
      playerDamageDealt = finalDmg;
      narrativeParts.push(`You strike for ${finalDmg} damage.`);
      break;
    }
    case 'defend': {
      narrativeParts.push('You brace for the enemy attack.');
      break;
    }
    case 'ability': {
      if (ability) {
        abilityUsed = ability.name;
        playerFrostbiteGain += ability.frostbiteCost;
        const totalAtk = ability.damage + playerStanceMods.attack + playerEffectAtk;
        const enemyTotalDef = enemyStanceMods.defense + enemyEffectDef + input.enemyDef.defense;
        playerDamageDealt = Math.max(1, totalAtk - Math.floor(enemyTotalDef * 0.5));
        narrativeParts.push(`You unleash ${ability.name} for ${playerDamageDealt} damage! (${ability.frostbiteCost} frostbite)`);
      }
      break;
    }
    case 'item': {
      itemUsed = playerAction.targetId;
      narrativeParts.push('You use an item.');
      break;
    }
    case 'flee': {
      // Flee is resolved separately
      break;
    }
  }

  // ─── Enemy Action ───────────────────────────────────────

  switch (enemyAction.type) {
    case 'attack': {
      const totalAtk = enemyDef.attack + enemyStanceMods.attack + enemyEffectAtk;
      const playerDef = playerDefense + playerStanceMods.defense + playerEffectDef;
      // If player is defending, apply reduction
      const rawDmg = Math.max(1, totalAtk - playerDef);
      const finalDmg = playerAction.type === 'defend'
        ? Math.max(1, Math.floor(rawDmg * DEFENSE_DAMAGE_REDUCTION))
        : rawDmg;
      playerDamageTaken = finalDmg;
      playerFrostbiteGain += enemyDef.frostbiteDamage;
      narrativeParts.push(`${enemyDef.name} strikes for ${finalDmg} damage.`);
      break;
    }
    case 'defend': {
      narrativeParts.push(`${enemyDef.name} takes a defensive stance.`);
      break;
    }
    case 'flee': {
      narrativeParts.push(`${enemyDef.name} tries to withdraw!`);
      break;
    }
    default:
      break;
  }

  // ─── Stance Shift ───────────────────────────────────────

  const stanceShift = calculateStanceShift(playerAction.type, enemyAction.type);
  const newPlayerStance = shiftStance(player.stance, stanceShift);
  // Enemy stance mirrors player inversely
  const newEnemyStance = shiftStance(enemy.stance, -stanceShift);

  if (stanceShift > 0) {
    narrativeParts.push('You gain tactical ground!');
  } else if (stanceShift < 0) {
    narrativeParts.push('You lose tactical ground!');
  }

  // ─── Apply HP Changes ──────────────────────────────────

  const newPlayerHP = Math.max(0, player.hp - playerDamageTaken);
  const newEnemyHP = Math.max(0, enemy.hp - playerDamageDealt);

  // ─── Tick down active effects ──────────────────────────

  tickEffects(player.effects);
  tickEffects(enemy.effects);

  return {
    round,
    playerAction,
    enemyAction,
    playerDamageDealt,
    playerDamageTaken,
    playerFrostbiteGain,
    stanceShift,
    narrative: narrativeParts.join(' '),
    playerStance: newPlayerStance,
    enemyStance: newEnemyStance,
    playerHP: newPlayerHP,
    enemyHP: newEnemyHP,
    abilityUsed,
    itemUsed,
  };
}

// ─── Flee Resolution ────────────────────────────────────────────

/**
 * Determine if a flee attempt succeeds.
 * Higher flee modifier (from stance) = better chance.
 */
export function resolveFlee(
  playerStance: CombatStance,
  enemyAction: CombatAction,
): boolean {
  const fleeMod = getStanceModifiers(playerStance).flee;
  let chance = FLEE_BASE_CHANCE + (fleeMod * FLEE_STANCE_BONUS);

  // Bonus if enemy is also fleeing or defending
  if (enemyAction.type === 'flee' || enemyAction.type === 'defend') {
    chance += 0.2;
  }

  return Math.random() < Math.max(0.1, Math.min(0.9, chance));
}

// ─── Stat Derivation ────────────────────────────────────────────

/**
 * Derive base attack value from character stats.
 * STR-based classes use strength, INT-based use intelligence, etc.
 */
export function derivePlayerAttack(stats: Record<string, number>, primaryStat: string): number {
  const primary = stats[primaryStat] ?? 10;
  const str = stats['strength'] ?? 10;
  return BASE_DAMAGE + Math.floor((primary + str) / 4);
}

/**
 * Derive base defense value from character stats.
 */
export function derivePlayerDefense(stats: Record<string, number>): number {
  const con = stats['constitution'] ?? 10;
  const dex = stats['dexterity'] ?? 10;
  return Math.floor((con + dex) / 4);
}

// ─── Combat Result Determination ────────────────────────────────

/**
 * Determine if combat should end and what the outcome is.
 */
export function checkCombatEnd(
  playerHP: number,
  enemyHP: number,
  playerFrostbite: number,
  fled: boolean,
): CombatOutcome | null {
  if (fled) return 'fled';
  if (playerHP <= 0 || playerFrostbite >= 100) return 'defeat';
  if (enemyHP <= 0) return 'victory';
  return null;
}

/**
 * Build the final combat result.
 */
export function buildCombatResult(
  outcome: CombatOutcome,
  rounds: number,
  hpLost: number,
  frostbiteGained: number,
  enemyDef: EnemyDefinition,
): CombatResult {
  return {
    outcome,
    rounds,
    xpEarned: outcome === 'victory' ? enemyDef.xpReward : 0,
    goldEarned: outcome === 'victory' ? enemyDef.goldReward : 0,
    lootDropped: outcome === 'victory' ? enemyDef.loot : [],
    hpLost,
    frostbiteGained,
    enemyName: enemyDef.name,
  };
}

// ─── Helpers ────────────────────────────────────────────────────

function sumEffectModifiers(effects: ActiveEffect[], stat: 'attack' | 'defense'): number {
  return effects
    .filter(e => e.modifier?.stat === stat)
    .reduce((sum, e) => sum + (e.modifier?.value ?? 0), 0);
}

function tickEffects(effects: ActiveEffect[]): void {
  for (let i = effects.length - 1; i >= 0; i--) {
    effects[i].duration--;
    if (effects[i].duration <= 0) {
      effects.splice(i, 1);
    }
  }
}
